/**
 * PreferencesPanel — User Preferences modal.
 * Conformed to the Spec 197 design blueprint: uses the canonical kit <Modal>
 * (transparent-but-blocking backdrop, framed, movable, no outside-close) and
 * design tokens instead of the hand-rolled opaque-backdrop dialog.
 *
 * Spec 253: section navigation is a dropdown (General / Email Preferences /
 * Favorites); the Email Preferences section embeds the self-service email
 * preference center (EmailPreferencesContent) directly.
 */
import React, { useState, useEffect } from 'react';
import { Settings, Palette, Home, Mail, Star } from 'lucide-react';
import { clsx } from 'clsx';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useTheme } from '../../theme/ThemeProvider';
import { FavoritesManager } from './FavoritesManager';
import { EmailPreferencesContent } from './EmailPreferencesContent';
import { Modal, Button } from '../ui';
import type { LandingPage } from '../../types/shell';

type PrefSection = 'general' | 'email' | 'favorites';

interface PreferencesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Section to open on (e.g. 'email' from an email deep-link). */
  initialSection?: PrefSection;
}

const SECTION_TABS: Array<{ key: PrefSection; label: string; icon: typeof Settings }> = [
  { key: 'general', label: 'General', icon: Settings },
  { key: 'email', label: 'Email Preferences', icon: Mail },
  { key: 'favorites', label: 'Favorites', icon: Star },
];

const THEME_OPTIONS = [
  { value: 'light' as const, label: 'Light', icon: '☀️' },
  { value: 'dark' as const, label: 'Dark', icon: '🌙' },
  { value: 'system' as const, label: 'System', icon: '🖥️' },
];

const LANDING_PAGE_OPTIONS = [
  { value: 'dashboard' as LandingPage, label: 'Dashboard', description: 'Overview and key metrics' },
  { value: 'recent-activity' as LandingPage, label: 'Recent Activity', description: 'Your latest actions' },
  { value: 'favorites' as LandingPage, label: 'Favorites', description: 'Your bookmarked pages' },
  { value: 'last-visited' as LandingPage, label: 'Last Visited', description: 'Resume where you left off' },
];

const selectedTile = 'border-[var(--color-accent)] bg-blue-50 dark:bg-blue-900/20';
const unselectedTile = 'border-[var(--color-border)] hover:border-[var(--color-text-secondary)]';

export const PreferencesPanel: React.FC<PreferencesPanelProps> = ({ isOpen, onClose, initialSection }) => {
  const { preferences, updatePreferences } = useUserPreferences();
  const { mode: currentTheme, setMode: setTheme } = useTheme();
  const [activeSection, setActiveSection] = useState<PrefSection>(initialSection ?? 'general');

  // Jump to the requested section when opened via a deep-link.
  useEffect(() => {
    if (isOpen && initialSection) setActiveSection(initialSection);
  }, [isOpen, initialSection]);
  const [localTheme, setLocalTheme] = useState(currentTheme);
  const [localLandingPage, setLocalLandingPage] = useState<LandingPage>(
    preferences.defaultLandingPage ?? 'dashboard',
  );

  const handleSave = () => {
    setTheme(localTheme);
    updatePreferences({ theme: localTheme, defaultLandingPage: localLandingPage });
    onClose();
  };

  const handleCancel = () => {
    setLocalTheme(currentTheme);
    setLocalLandingPage(preferences.defaultLandingPage ?? 'dashboard');
    onClose();
  };

  const handleReset = () => {
    setLocalTheme('system');
    setLocalLandingPage('dashboard');
    updatePreferences({ theme: 'system', defaultLandingPage: 'dashboard', favorites: [], sidebarCollapsed: false });
    setTheme('system');
  };

  // Email preferences auto-save per toggle, so that section only needs a Close action.
  const footer =
    activeSection === 'email' ? (
      <div className="flex w-full justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    ) : (
      <div className="flex w-full items-center justify-between">
        <button
          onClick={handleReset}
          className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
        >
          Reset to Defaults
        </button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </div>
      </div>
    );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-[var(--color-text-secondary)]" />
          User Preferences
        </span>
      }
      footer={footer}
    >
      {/* Section tabs (header across all preference sections) */}
      <div className="mb-5 flex gap-6 border-b border-[var(--color-border)]">
        {SECTION_TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveSection(key)}
            className={clsx(
              '-mb-px flex items-center gap-2 border-b-2 px-1 py-2 text-sm font-medium transition-colors',
              activeSection === key
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {activeSection === 'general' && (
        <div className="space-y-6">
          {/* Theme Selection */}
          <div>
            <div className="mb-3 flex items-center gap-2">
              <Palette className="h-4 w-4 text-[var(--color-text-secondary)]" />
              <label className="text-sm font-medium text-[var(--color-text-primary)]">Theme Preference</label>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {THEME_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setLocalTheme(option.value)}
                  className={clsx(
                    'relative rounded-lg border-2 p-4 text-center transition-colors',
                    localTheme === option.value ? selectedTile : unselectedTile,
                  )}
                >
                  <div className="mb-1 text-2xl">{option.icon}</div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)]">{option.label}</div>
                  {localTheme === option.value && (
                    <div className="absolute right-2 top-2 h-3 w-3 rounded-full bg-[var(--color-accent)]" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Default Landing Page */}
          <div>
            <div className="mb-3 flex items-center gap-2">
              <Home className="h-4 w-4 text-[var(--color-text-secondary)]" />
              <label className="text-sm font-medium text-[var(--color-text-primary)]">Default Landing Page</label>
            </div>
            <div className="space-y-2">
              {LANDING_PAGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setLocalLandingPage(option.value)}
                  className={clsx(
                    'w-full rounded-lg border p-3 text-left transition-colors',
                    localLandingPage === option.value ? selectedTile : unselectedTile,
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-[var(--color-text-primary)]">{option.label}</div>
                      <div className="text-sm text-[var(--color-text-secondary)]">{option.description}</div>
                    </div>
                    {localLandingPage === option.value && (
                      <div className="h-3 w-3 flex-shrink-0 rounded-full bg-[var(--color-accent)]" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeSection === 'email' && <EmailPreferencesContent />}

      {activeSection === 'favorites' && <FavoritesManager />}
    </Modal>
  );
};

export default PreferencesPanel;
